/**
 * <s-popup> — timed, dismissible promo popup.
 * Opens its <dialog> modally after `delay` seconds unless the visitor closed it
 * within the last `hide-days` days. Closing (button, Esc, backdrop) records the time.
 *
 * Attributes:
 *   delay      — seconds before opening (default 6)
 *   hide-days  — days to stay hidden after closing (default 7)
 *   storage-key — localStorage key (default "s-popup-dismissed")
 *   preview    — open immediately and ignore dismissal (editor preview)
 */
class SPopup extends HTMLElement {
  #dialog;
  #timer;
  #ac;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dialog.addEventListener('close', () => this.#remember(), opts);
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);

    if (this.hasAttribute('preview')) {
      this.#open();
      return;
    }
    if (this.#recentlyDismissed()) return;

    const delay = Math.max(0, Number(this.getAttribute('delay') ?? 6)) * 1000;
    this.#timer = setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  get #key() {
    return this.getAttribute('storage-key') || 's-popup-dismissed';
  }

  #open() {
    if (this.#dialog.open) return;
    // Don't stack on top of another open modal (cart drawer, search, menu).
    if (document.querySelector('dialog[open]:modal')) return;
    this.#dialog.showModal();
  }

  #remember() {
    if (this.hasAttribute('preview')) return;
    try {
      localStorage.setItem(this.#key, String(Date.now()));
    } catch {
      /* storage unavailable: popup may show again next visit */
    }
  }

  #recentlyDismissed() {
    try {
      const stored = Number(localStorage.getItem(this.#key));
      if (!stored) return false;
      const days = Number(this.getAttribute('hide-days') ?? 7);
      return Date.now() - stored < days * 86400000;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('s-popup')) customElements.define('s-popup', SPopup);
